import http from 'api/httpClient';

export type BillSortKey =
  | 'reference_no'
  | 'branch_name'
  | 'bill_date'
  | 'due_date'
  | 'ifn_no'
  | 'ifn_date'
  | 'overdue_days'
  | 'outstanding'
  | 'transaction_amt';

export type AraUserSortKey =
  | 'collection_responsibility'
  | 'number_of_bills'
  | 'total_outstanding'
  | 'not_due'
  | 'total_overdue'
  | 'bucket_1_30'
  | 'bucket_31_60'
  | 'bucket_61_90'
  | 'bucket_91_120'
  | 'bucket_121_180'
  | 'bucket_over_180'
  | 'legal';

export type DueStatus = 'not_due' | 'overdue';

export type AraYearMonths = {
  year: string;
  months: Record<number, number[]>;
};

export type FetchAraYearsResponse = {
  years: AraYearMonths[];
};

export interface Subordinate {
  ecode: string;
  full_name: string;
}

export interface SubordinatesResponse {
  items: Subordinate[];
}

export interface Division {
  ou_code: string;
  ou_name: string;
}

export interface DivisionsResponse {
  items: Division[];
}

export type PaymentType = 'to_pay' | 'to_be_billed' | 'non_cn';
export type PaymentTypeValue = PaymentType | '';

export type CustomerStatusType = 'active' | 'inactive' | 'legal';
export type CustomerStatusTypeValue = CustomerStatusType | '';

export interface AraTableRow {
  Customer: string;
  CreditPeriod: number;
  TotalOutstanding: number;
  NotDue: number;
  TotalOverdue: number;
  Bucket1_30: number;
  Bucket31_60: number;
  Bucket61_90: number;
  Bucket91_120: number;
  Bucket121_180: number;
  BucketOver180: number;
  Legal: number;
  UnappliedReceipts: number;
  NetOutstanding: number;
  DSO: number | null;
}

export interface FetchAraRequest extends Record<string, unknown> {
  year: string;
  month: number;
  day: number;
  ecode?: string;
  division?: string;
  payment_type?: PaymentTypeValue;
  customer_status?: CustomerStatusTypeValue;
  from_date?: string;
  to_date?: string;
  page: number;
  per_page: number;
  sort_by: string;
  sort_order: 'asc' | 'desc';
  search: string | undefined;
}

export interface FetchAraResponse {
  rows: AraTableRow[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_previous: boolean;
  has_next: boolean;
  selected_user: string;
  subordinates: string[];
}

export interface DownloadAgeingDataRequest extends Record<string, unknown> {
  year: string;
  month: number;
  day: number;
  ecode?: string;
  division?: string;
  payment_type?: PaymentTypeValue;
  customer_status?: CustomerStatusTypeValue;
  consignor_code?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface BillDetailRow {
  reference_no: string;
  ifn_no: string | null;
  branch_name: string | null;
  bill_date: string | null;
  due_date: string | null;
  ifn_date: string | null;
  overdue_days: number | null;
  outstanding: number;
  transaction_amt: number;
  collection_responsibility: string | null;
}

export interface BillDetailsResponse {
  customer_name: string;
  customer_code: string;
  items: BillDetailRow[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_previous: boolean;
  has_next: boolean;
}

export interface FetchAraByUserRequest extends Record<string, unknown> {
  customer_name: string;
  year: string;
  month: number;
  day: number;
  division?: string;
  ecode?: string;
  from_date?: string;
  to_date?: string;
  page: number;
  per_page: number;
}

export interface AraUserTableRow {
  CollectionResponsibility: string;
  CollectionResponsibilityEcode: string | null;
  EmployeePhotoUrl?: string | null;
  NumberOfBills: number;
  TotalOutstanding: number;
  NotDue: number;
  TotalOverdue: number;
  Bucket1_30: number;
  Bucket31_60: number;
  Bucket61_90: number;
  Bucket91_120: number;
  Bucket121_180: number;
  BucketOver180: number;
  Legal: number;
}

export interface FetchAraByUserResponse {
  rows: AraUserTableRow[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_previous: boolean;
  has_next: boolean;
}

export interface Consignor {
  cust_code: string;
  cust_name: string;
}

export interface ConsignorsResponse {
  items: Consignor[];
}

export async function fetchAraYears(): Promise<FetchAraYearsResponse> {
  return http.get('/api/v1/periods/snapshots?source=outstanding');
}

export async function fetchDivisions(): Promise<DivisionsResponse> {
  return http.get('/api/v1/outstanding/divisions');
}

export async function fetchSubordinates(divisionOuCode?: string): Promise<SubordinatesResponse> {
  const params = new URLSearchParams();
  if (divisionOuCode) {
    params.set('division_ou_code', divisionOuCode);
  }
  const url = `/api/v1/outstanding/subordinates${params.toString() ? `?${params.toString()}` : ''}`;
  return http.get(url);
}

export async function fetchAraData(
  payload: FetchAraRequest
): Promise<FetchAraResponse> {
  const data = await http.post<FetchAraResponse>(
    '/api/v1/outstanding/ageing',
    payload as Record<string, unknown>
  );

  const normalizedRows = (data.rows || []).map((r: any) => ({
    ...r,
    Bucket1_30: r['1-30 days'] ?? 0,
    Bucket31_60: r['31-60 days'] ?? 0,
    Bucket61_90: r['61-90 days'] ?? 0,
    Bucket91_120: r['91-120 days'] ?? 0,
    Bucket121_180: r['121-180 days'] ?? 0,
    BucketOver180: r['> 180 days'] ?? 0,
  }));

  return {
    ...data,
    rows: normalizedRows,
  };
}

export async function downloadBulkAgeingData(payload: DownloadAgeingDataRequest): Promise<Blob> {
  return http.post('/api/v1/outstanding/download-bulk-ageing-data', payload, { responseType: 'blob' });
}

export async function fetchAgeingByUser(payload: FetchAraByUserRequest): Promise<FetchAraByUserResponse> {
  return http.post('/api/v1/outstanding/ageing-by-user', payload);
}


export interface DownloadAgeingPageDataRequest extends Record<string, unknown> {
  year: string;
  month: number;
  day: number;
  ecode?: string;
  division?: string;
  payment_type?: PaymentTypeValue;
  customer_status?: CustomerStatusTypeValue;
  consignor_code?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export async function downloadAgeingData(payload: DownloadAgeingPageDataRequest): Promise<Blob> {
  return http.post('/api/v1/outstanding/download-ageing-data', payload, { responseType: 'blob' });
}

export interface DownloadRDDProvisionRequest extends Record<string, unknown> {
  year: string;
  month: number;
  day: number;
}

export async function downloadRDDProvision(payload: DownloadRDDProvisionRequest): Promise<Blob> {
  return http.post('/api/v1/outstanding/download-rdd-provision', payload, { responseType: 'blob' });
}

export async function fetchBillDetails(payload: {
  customer_name: string;
  year: string;
  month: number;
  day: number;
  ecode: string | undefined;
  division: string | undefined;
  search: string | undefined;
  due_status?: DueStatus;
  from_date?: string;
  to_date?: string;
  page: number;
  per_page: number;
  sort_by: string;
  sort_order: 'asc' | 'desc';
}): Promise<BillDetailsResponse> {
  return http.post<BillDetailsResponse>('/api/v1/outstanding/get-bill-details', payload);
}


export async function downloadBillDetails(payload: {
  customer_name: string;
  year: string;
  month: number;
  day: number;
  ecode?: string;
  division?: string;
  search?: string;
  due_status?: DueStatus;
  from_date?: string;
  to_date?: string;
  sort_by: string;
  sort_order: 'asc' | 'desc';
}): Promise<Blob> {
  return http.post('/api/v1/outstanding/download-bill-details', payload, { responseType: 'blob' });
}

export async function fetchConsignors({
  year,
  month,
  day,
  ecode,
}: {
  year: string;
  month: number;
  day: number;
  ecode?: string;
}): Promise<ConsignorsResponse> {
  const params = new URLSearchParams();
  params.set('year', year);
  params.set('month', String(month));
  params.set('day', String(day));
  if (ecode) {
    params.set('ecode', ecode);
  }
  return http.get<ConsignorsResponse>(`/api/v1/outstanding/get-consignors?${params.toString()}`);
}